/** Clinio Forms admin UI (staff-facing questionnaire management). */
export const FORMS_ADMIN_BASE_URL = 'https://forms.clinio.dk';

/** Patient-facing questionnaire fill-out host. */
export const FORMS_PATIENT_BASE_URL = 'https://forms.clinio.dk';

/**
 * Staff login landing page. Same origin as the admin UI — this repo has no
 * separate `/login` path (SSO uses `/dashboard` after a grant).
 */
export const FORMS_LOGIN_URL = FORMS_ADMIN_BASE_URL;

export type FormsAdminUrlOptions = {
  /** Forms tenant UUID — selects the clinic-linked workspace after login. */
  tenantId?: string | null;
  /** Path under the Forms origin, e.g. `/questionnaires/<id>/edit`. */
  path?: string;
};

/** Default Forms landing path after SSO (Forms PR #6). */
export const FORMS_SSO_DASHBOARD_PATH = '/dashboard';

/** Edit deep-link for a questionnaire in Clinio Forms. */
export function formsQuestionnaireEditPath(templateId: string): string {
  return `/questionnaires/${templateId}/edit`;
}

/** Relative Forms admin path used as `redirect_path` for SSO. */
export function formsAdminPath(path?: string | null): string {
  if (!path) return FORMS_SSO_DASHBOARD_PATH;
  return path.startsWith('/') ? path : `/${path}`;
}

/**
 * Build a Forms admin URL. Staff should open Forms via SSO (`forms-sso-link`),
 * not this helper. Kept for tests and non-staff links.
 * When `tenantId` is set, appends `?tenant=` (legacy; SSO replaces this).
 */
export function formsAdminUrl(options: FormsAdminUrlOptions = {}): string {
  const path = formsAdminPath(options.path);
  const url = new URL(path, FORMS_ADMIN_BASE_URL);
  if (options.tenantId) {
    url.searchParams.set('tenant', options.tenantId);
  }
  return url.toString();
}

export function formsResponseUrl(formsAssignmentId: string): string {
  return `${FORMS_ADMIN_BASE_URL}/responses/${formsAssignmentId}`;
}

/** Normalizes legacy forms.clinio.app links to forms.clinio.dk. */
export function normalizePatientFormsUrl(url: string): string {
  try {
    const parsed = new URL(url);
    if (parsed.hostname === 'forms.clinio.app' || parsed.hostname === 'forms.clinio.dk') {
      parsed.protocol = 'https:';
      parsed.hostname = 'forms.clinio.dk';
      return parsed.toString();
    }
    return url;
  } catch {
    return url.replace('https://forms.clinio.app/', `${FORMS_PATIENT_BASE_URL}/`);
  }
}

/** Reads cached public fill-out URL from patient_forms_assignments.metadata. */
export function publicUrlFromAssignmentMetadata(metadata: unknown): string | null {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) return null;
  const url = (metadata as Record<string, unknown>).public_url;
  if (typeof url !== 'string' || !url.startsWith('https://')) return null;
  return normalizePatientFormsUrl(url);
}
